import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import api from '../api/client';
import { useAuthStore } from '../store/auth';

interface TenantInfo {
  id: string;
  name: string;
  plan: string;
  settings: Record<string, any>;
  _count: { users: number; projects: number; customers: number };
}

interface UserInfo {
  id: string;
  email: string;
  name: string;
  role: string;
  active: boolean;
}

interface LicenseInfo {
  hasLicense: boolean;
  id?: string;
  key?: string;
  plan?: string;
  maxSeats?: number;
  includedSeats?: number;
  usedSeats: number;
  basePrice?: number;
  seatPrice?: number;
  billingCycle?: string;
  validFrom?: string;
  validUntil?: string;
  active?: boolean;
  isExpired?: boolean;
  daysRemaining?: number;
  extraSeats?: number;
  monthlyCost?: number;
}

const roleLabels: Record<string, string> = {
  SUPERADMIN: 'Super-Admin', ADMIN: 'Admin', OFFICE: 'Büro', WORKER: 'Bauarbeiter',
};

const roleStyles: Record<string, string> = {
  SUPERADMIN: 'bg-red-50 text-red-700 border border-red-200',
  ADMIN:      'bg-brand-50 text-brand-700 border border-brand-200',
  OFFICE:     'bg-amber-50 text-amber-700 border border-amber-200',
  WORKER:     'bg-slate-100 text-slate-600',
};

const PRICING = {
  BRONZE: {
    id: 'BRONZE',
    label: 'Handwerker',
    subtitle: 'Für kleine Betriebe',
    basePrice: 99,
    seatPrice: 49,
    includedSeats: 1,
    freeMonths: 1,
    extraSeatsAllowed: true,
    gradient: 'from-amber-600 to-orange-500',
    border: 'border-amber-300',
    bg: 'bg-gradient-to-b from-amber-50/50 to-white',
    badge: null as string | null,
    features: [
      'Büro-WebApp (alle Module)',
      'Kunden- & Projektverwaltung',
      'Angebote & Rechnungen mit PDF',
      'Mahnwesen (3-stufig)',
      'Nachkalkulation',
      'Lieferscheine',
      '1 Nutzer inklusive',
      'Weitere Nutzer: 49 €/Monat',
    ],
    notIncluded: [
      'Android Tablet-App',
      'GAEB / DATANORM / DATEV',
      'XRechnung / ZUGFeRD',
      'KI-Assistent',
    ],
  },
  SILVER: {
    id: 'SILVER',
    label: 'Meister',
    subtitle: 'Für wachsende Betriebe',
    basePrice: 199,
    seatPrice: 59,
    includedSeats: 1,
    freeMonths: 2,
    extraSeatsAllowed: true,
    gradient: 'from-brand-500 to-brand-600',
    border: 'border-brand-400',
    bg: 'bg-gradient-to-b from-brand-50/50 to-white',
    badge: 'Beliebt' as string | null,
    features: [
      'Alles aus Handwerker',
      'Android Tablet-App (Offline)',
      'GAEB D83/D84/D86 Import/Export',
      'DATANORM v4 & v5 Kataloge',
      'DATEV-Export (Buchhaltung + Lohn)',
      'XRechnung / ZUGFeRD',
      'Plantafel & Aufmaß',
      'Abschlagsrechnungen',
      'Digitale Unterschriften',
      'Bauberichte vom Tablet',
      '1 Nutzer inklusive',
      'Weitere Nutzer: 59 €/Monat',
    ],
    notIncluded: [
      'KI-Assistent',
      'KI-Rechnungsscan (OCR)',
    ],
  },
  GOLD: {
    id: 'GOLD',
    label: 'Betrieb',
    subtitle: 'Für große Unternehmen',
    basePrice: 299,
    seatPrice: 69,
    includedSeats: 1,
    freeMonths: 2,
    extraSeatsAllowed: true,
    gradient: 'from-yellow-500 to-amber-400',
    border: 'border-yellow-400',
    bg: 'bg-gradient-to-b from-yellow-50/50 to-white',
    badge: 'Maximal' as string | null,
    features: [
      'Alles aus Meister',
      'KI-Assistent (Claude)',
      'KI-Rechnungsscan (OCR)',
      'KI-Angebotspositionen aus Freitext',
      'Priority-Support',
      'Individuelle Anpassungen',
      '1 Nutzer inklusive',
      'Weitere Nutzer: 69 €/Monat',
    ],
    notIncluded: [],
  },
};

const planOrder = ['BRONZE', 'SILVER', 'GOLD'] as const;

export default function SettingsPage() {
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [licenseKeyInput, setLicenseKeyInput] = useState('');

  const { data: tenant } = useQuery<TenantInfo>({
    queryKey: ['tenant'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
  });

  const { data: users = [] } = useQuery<UserInfo[]>({
    queryKey: ['tenant-users'],
    queryFn: () => api.get('/tenant/users').then((r) => r.data),
  });

  const { data: license } = useQuery<LicenseInfo>({
    queryKey: ['license'],
    queryFn: () => api.get('/license').then((r) => r.data),
  });

  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'WORKER' });
  const [showNewUser, setShowNewUser] = useState(false);

  const createUser = useMutation({
    mutationFn: () => api.post('/tenant/users', newUser),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-users'] });
      qc.invalidateQueries({ queryKey: ['license'] });
      setShowNewUser(false);
      setNewUser({ email: '', name: '', password: '', role: 'WORKER' });
    },
  });

  const activateLicense = useMutation({
    mutationFn: (key: string) => api.post('/license/activate', { key }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license'] });
      qc.invalidateQueries({ queryKey: ['tenant'] });
      setLicenseKeyInput('');
    },
  });

  const seatPercent = license?.hasLicense && license.maxSeats
    ? Math.min(100, Math.round((license.usedSeats / license.maxSeats) * 100))
    : 0;

  const isSuperAdmin = authUser?.role === 'SUPERADMIN';

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <h1 className="sp-page-title">Einstellungen</h1>
        <p className="sp-page-subtitle">Verwalten Sie Ihren Betrieb, Ihr Team und Ihre Lizenz</p>
      </div>

      {/* Firm info */}
      <div className="sp-card p-6 mb-5 animate-fade-in">
        <h2 className="sp-section-title mb-5">Firma</h2>
        <div className="grid grid-cols-3 gap-8">
          <InfoBlock label="Firmenname" value={tenant?.name} />
          <InfoBlock label="Projekte" value={tenant?._count.projects} />
          <InfoBlock label="Kunden" value={tenant?._count.customers} />
        </div>
      </div>

      {/* ═══ LIZENZ-STATUS ═══ */}
      <div className="sp-card p-6 mb-5 animate-fade-in" style={{ animationDelay: '80ms' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="sp-section-title">Lizenz</h2>
          {license?.hasLicense && license.active && (
            <span className="sp-badge bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" />
              Aktiv
            </span>
          )}
          {license?.hasLicense && license.isExpired && (
            <span className="sp-badge bg-error-50 text-error-700 border border-error-200">Abgelaufen</span>
          )}
          {license && !license.hasLicense && (
            <span className="sp-badge bg-amber-50 text-amber-700 border border-amber-200">Keine Lizenz</span>
          )}
        </div>

        {license?.hasLicense ? (
          <div className="space-y-5">
            {/* License key + plan */}
            <div className="grid grid-cols-4 gap-6">
              <InfoBlock label="Lizenzschlüssel" value={license.key || '–'} />
              <InfoBlock label="Plan" value={PRICING[license.plan as keyof typeof PRICING]?.label || license.plan || '–'} />
              <InfoBlock label="Abrechnungszyklus" value={license.billingCycle === 'ANNUAL' ? 'Jährlich' : 'Monatlich'} />
              <InfoBlock label="Gültig bis" value={license.validUntil ? new Date(license.validUntil).toLocaleDateString('de-DE') : '–'} />
            </div>

            {/* Seats usage */}
            <div className="p-4 rounded-xl bg-surface-50 border border-surface-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] font-semibold text-slate-700">
                  Nutzer
                </div>
                <div className="text-xs text-slate-500">
                  <span className="font-bold text-slate-800">{license.usedSeats}</span> / {license.maxSeats} belegt
                </div>
              </div>
              <div className="w-full h-3 bg-surface-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    seatPercent >= 90 ? 'bg-error-500' : seatPercent >= 70 ? 'bg-amber-500' : 'bg-brand-500'
                  }`}
                  style={{ width: `${seatPercent}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="text-[11px] text-slate-400">
                  {license.includedSeats} Nutzer im Grundpreis · {(license.extraSeats || 0) > 0
                    ? `${license.extraSeats} zusätzliche Nutzer à ${license.seatPrice} €/Mo`
                    : 'Keine zusätzlichen Nutzer'
                  }
                </div>
                {seatPercent >= 90 && (
                  <span className="text-[10px] font-semibold text-error-600">Fast voll!</span>
                )}
              </div>
            </div>

            {/* Monthly cost summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-brand-50/50 border border-brand-100">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Grundgebühr</div>
                <div className="text-lg font-bold text-slate-900">{license.basePrice} €<span className="text-xs font-normal text-slate-400">/Mo</span></div>
              </div>
              <div className="p-4 rounded-xl bg-brand-50/50 border border-brand-100">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Nutzer</div>
                <div className="text-lg font-bold text-slate-900">
                  {(license.extraSeats || 0) > 0 ? `${(license.extraSeats || 0) * (license.seatPrice || 0)} €` : '0 €'}
                  <span className="text-xs font-normal text-slate-400">/Mo</span>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100">
                <div className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">Gesamt monatlich</div>
                <div className="text-lg font-bold text-emerald-700">{license.monthlyCost} €<span className="text-xs font-normal text-slate-400">/Mo</span></div>
              </div>
            </div>

            {/* Remaining days warning */}
            {license.daysRemaining !== undefined && license.daysRemaining <= 30 && !license.isExpired && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-xs text-amber-800">
                  <span className="font-semibold">Ihre Lizenz läuft in {license.daysRemaining} Tagen ab.</span>{' '}
                  Bitte kontaktieren Sie uns zur Verlängerung.
                </div>
              </div>
            )}
          </div>
        ) : (
          /* No license — activation form */
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <div>
                  <div className="text-[13px] font-semibold text-amber-800 mb-1">Lizenz aktivieren</div>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Geben Sie Ihren Lizenzschlüssel ein, den Sie per E-Mail erhalten haben.
                    Noch keine Lizenz? Wählen Sie unten einen Plan und kontaktieren Sie uns.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input
                value={licenseKeyInput}
                onChange={(e) => setLicenseKeyInput(e.target.value.toUpperCase())}
                placeholder="SP-XXXX-XXXX-XXXX"
                className="sp-input flex-1 font-mono tracking-wider text-center text-sm"
              />
              <button
                onClick={() => activateLicense.mutate(licenseKeyInput)}
                disabled={!licenseKeyInput.trim() || activateLicense.isPending}
                className="sp-btn-primary"
              >
                {activateLicense.isPending ? 'Prüfe...' : 'Aktivieren'}
              </button>
            </div>
            {activateLicense.isError && (
              <p className="text-xs text-error-600 font-medium">
                Lizenzschlüssel ungültig oder nicht zugeordnet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Users */}
      <div className="sp-card overflow-hidden animate-fade-in mb-5" style={{ animationDelay: '150ms' }}>
        <div className="px-6 py-5 flex items-center justify-between border-b border-surface-100">
          <div>
            <h2 className="sp-section-title">Nutzerverwaltung</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {users.length} Nutzer registriert
              {license?.hasLicense && ` · ${license.maxSeats! - license.usedSeats} Nutzer frei`}
            </p>
          </div>
          <button
            onClick={() => setShowNewUser(true)}
            className="sp-btn-primary text-xs"
            disabled={license?.hasLicense && license.usedSeats >= (license.maxSeats || 999)}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nutzer anlegen
          </button>
        </div>

        {showNewUser && (
          <div className="px-6 py-5 bg-brand-50/30 border-b border-brand-100 animate-scale-in origin-top">
            <h3 className="text-[13px] font-semibold text-slate-700 mb-4">Neuen Nutzer anlegen</h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="sp-label text-[11px]">Name</label>
                <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  className="sp-input text-xs" placeholder="Vor- und Nachname" autoFocus />
              </div>
              <div>
                <label className="sp-label text-[11px]">E-Mail</label>
                <input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="sp-input text-xs" placeholder="name@firma.de" />
              </div>
              <div>
                <label className="sp-label text-[11px]">Passwort</label>
                <input type="password" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="sp-input text-xs" placeholder="Min. 8 Zeichen" />
              </div>
              <div>
                <label className="sp-label text-[11px]">Rolle</label>
                <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="sp-input text-xs">
                  <option value="ADMIN">Admin</option>
                  <option value="OFFICE">Büro</option>
                  <option value="WORKER">Bauarbeiter</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button onClick={() => createUser.mutate()} className="sp-btn-primary text-xs">Anlegen</button>
              <button onClick={() => setShowNewUser(false)} className="sp-btn-secondary text-xs">Abbrechen</button>
              {createUser.isError && (
                <span className="text-xs text-error-600">
                  {(createUser.error as any)?.response?.data?.code === 'SEAT_LIMIT'
                    ? 'Nutzerlimit erreicht! Bitte upgraden.'
                    : 'Fehler beim Anlegen'}
                </span>
              )}
            </div>
          </div>
        )}

        <table className="sp-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-100 to-purple-100 flex items-center justify-center text-[11px] font-bold text-brand-600 flex-shrink-0">
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-slate-800">{u.name}</span>
                  </div>
                </td>
                <td className="text-slate-500">{u.email}</td>
                <td>
                  <span className={`sp-badge ${roleStyles[u.role] || 'bg-slate-100 text-slate-600'}`}>
                    {roleLabels[u.role] || u.role}
                  </span>
                </td>
                <td>
                  {u.active ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Aktiv
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      Deaktiviert
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ ABO / PREISE ═══ */}
      <div className="animate-fade-in" style={{ animationDelay: '250ms' }}>
        <div className="sp-card p-6 mb-5">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="sp-section-title">Abo & Preise</h2>
              <p className="text-xs text-slate-400 mt-1">
                Grundpauschale + Nutzer · Alle Preise zzgl. MwSt.
              </p>
            </div>

            {/* Billing toggle */}
            <div className="flex items-center gap-1 bg-surface-100 rounded-xl p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Monatlich
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Jährlich
                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                  Bis zu 2 Monate gratis
                </span>
              </button>
            </div>
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-3 gap-4">
            {planOrder.map((planId) => {
              const plan = PRICING[planId];
              const isActive = tenant?.plan === planId;

              // Monthly base price; annual = 10 months for plans with free months, else 12
              const effectiveMonths = billingCycle === 'annual' ? (12 - plan.freeMonths) : 1;
              const annualBase = plan.basePrice * effectiveMonths;
              const monthlyDisplay = billingCycle === 'annual' ? Math.round(annualBase / 12) : plan.basePrice;

              return (
                <div
                  key={planId}
                  className={`relative rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                    isActive
                      ? `${plan.border} shadow-lg ring-1 ring-brand-200`
                      : 'border-surface-200 hover:border-surface-300 hover:shadow-md'
                  }`}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div className={`absolute top-0 right-0 px-3 py-1 text-[10px] font-bold text-white rounded-bl-xl bg-gradient-to-r ${plan.gradient}`}>
                      {plan.badge}
                    </div>
                  )}

                  {isActive && <div className={`h-1 bg-gradient-to-r ${plan.gradient}`} />}

                  <div className={`p-6 ${plan.bg}`}>
                    {/* Plan name */}
                    <div className="inline-flex items-center gap-2 mb-4">
                      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                          {planId === 'BRONZE' && (
                            <><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" /></>
                          )}
                          {planId === 'SILVER' && (
                            <><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></>
                          )}
                          {planId === 'GOLD' && (
                            <><path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></>
                          )}
                        </svg>
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-[15px]">{plan.label}</div>
                        <div className="text-[11px] text-slate-400">{plan.subtitle}</div>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-1">
                      <div className="flex items-baseline gap-1">
                        {billingCycle === 'annual' && plan.freeMonths > 0 && (
                          <span className="text-lg font-semibold text-slate-300 line-through mr-1">{plan.basePrice}</span>
                        )}
                        <span className="text-3xl font-extrabold text-slate-900">
                          {billingCycle === 'annual' ? monthlyDisplay : plan.basePrice}
                        </span>
                        <span className="text-sm text-slate-400 font-medium">€/Mo</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {plan.extraSeatsAllowed
                          ? `+ ${plan.seatPrice} € pro zusätzlichem Nutzer`
                          : 'Keine zusätzlichen Nutzer'}
                      </div>
                      {billingCycle === 'annual' && plan.freeMonths > 0 && (
                        <div className="mt-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
                          <div className="flex items-center gap-1.5 mb-1">
                            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-[11px] font-bold text-emerald-700">
                              {plan.freeMonths} {plan.freeMonths === 1 ? 'Monat' : 'Monate'} gratis
                            </span>
                          </div>
                          <div className="text-[11px] text-emerald-600">
                            {annualBase} €/Jahr statt {plan.basePrice * 12} € · <span className="font-bold">Du sparst {plan.basePrice * 12 - annualBase} €</span>
                          </div>
                        </div>
                      )}
                      {billingCycle === 'monthly' && plan.freeMonths > 0 && (
                        <div className="mt-2 p-2 rounded-lg bg-slate-50 border border-slate-100">
                          <div className="text-[10px] text-slate-400">
                            <span className="text-brand-500 font-semibold cursor-pointer" onClick={() => setBillingCycle('annual')}>
                              Jahresabo wählen → {plan.freeMonths === 1 ? '1 Monat' : `${plan.freeMonths} Monate`} gratis ({plan.basePrice * plan.freeMonths} € sparen)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Included seats */}
                    <div className="mt-3 mb-4 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-2 text-xs">
                        <svg className="w-4 h-4 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-semibold text-slate-700">
                          1 Nutzer inklusive
                        </span>
                        <span className="text-slate-400">unbegrenzt erweiterbar</span>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-surface-200 to-transparent mb-4" />

                    {/* Features */}
                    <div className="space-y-2">
                      {plan.features.map((f) => (
                        <div key={f} className="flex items-start gap-2">
                          <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-[11px] text-slate-700 leading-relaxed">{f}</span>
                        </div>
                      ))}
                      {plan.notIncluded.map((f) => (
                        <div key={f} className="flex items-start gap-2 opacity-35">
                          <svg className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span className="text-[11px] text-slate-400 leading-relaxed">{f}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-5">
                      {isActive ? (
                        <div className="w-full py-2.5 rounded-xl bg-brand-50 text-brand-700 text-xs font-semibold text-center border border-brand-200">
                          Aktueller Plan
                        </div>
                      ) : (
                        <a
                          href={`mailto:info@samoplan.de?subject=Lizenz ${plan.label}-Plan anfragen&body=Ich möchte den ${plan.label}-Plan (${billingCycle === 'annual' ? 'Jahresabo' : 'Monatsabo'}) buchen.%0A%0AGrundgebühr: ${plan.basePrice} €/Mo%0ANutzer gewünscht: ____%0A%0AFirma: ${tenant?.name || ''}%0ATenant-ID: ${tenant?.id || ''}`}
                          className={`w-full py-2.5 rounded-xl text-xs font-semibold text-center block transition-all ${
                            planId === 'GOLD'
                              ? `bg-gradient-to-r ${plan.gradient} text-white hover:shadow-lg hover:shadow-amber-200/50`
                              : planId === 'SILVER'
                                ? 'bg-brand-500 text-white hover:bg-brand-600 hover:shadow-lg hover:shadow-brand-200/50'
                                : 'bg-amber-600 text-white hover:bg-amber-700'
                          }`}
                        >
                          Lizenz anfragen
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Example calculation */}
          <div className="mt-6 p-5 rounded-xl bg-surface-50 border border-surface-100">
            <div className="text-[13px] font-semibold text-slate-800 mb-3">Rechenbeispiele</div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700 block mb-1">3 Nutzer, Handwerker</span>
                99 + 2×49 = <span className="font-bold text-slate-800">197 €/Mo</span>
                <span className="block text-[10px] text-emerald-600 mt-0.5">Jahr: 2.167 € (1 Mo gratis)</span>
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700 block mb-1">5 Nutzer, Meister</span>
                199 + 4×59 = <span className="font-bold text-slate-800">435 €/Mo</span>
                <span className="block text-[10px] text-emerald-600 mt-0.5">Jahr: 4.350 € (2 Mo gratis)</span>
              </div>
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700 block mb-1">10 Nutzer, Betrieb</span>
                299 + 9×69 = <span className="font-bold text-slate-800">920 €/Mo</span>
                <span className="block text-[10px] text-emerald-600 mt-0.5">Jahr: 9.200 € (2 Mo gratis)</span>
              </div>
            </div>
          </div>

          {/* Payment info */}
          <div className="mt-4 p-4 rounded-xl bg-surface-50 border border-surface-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 mb-1">Zahlung per Banküberweisung</div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  Nach der Lizenzanfrage erhalten Sie eine Rechnung per E-Mail. Zahlung bequem per
                  Überweisung innerhalb von 14 Tagen. Beim Jahresabo sparen Sie 1–2 Monatsgebühren je nach Plan.
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  Fragen? Schreiben Sie an{' '}
                  <a href="mailto:info@samoplan.de" className="text-brand-500 hover:text-brand-600 font-medium">info@samoplan.de</a>
                  {' '}oder rufen Sie an: <span className="font-medium text-slate-500">+49 123 456789</span>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Alle Preise zzgl. MwSt. · Jederzeit zum Monatsende kündbar · Keine Einrichtungsgebühr · DSGVO-konform (Server in Deutschland)
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value?: string | number }) {
  return (
    <div>
      <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5">{label}</div>
      <div className="text-[15px] font-semibold text-slate-800">{value ?? '-'}</div>
    </div>
  );
}
